import { PlaidTransaction } from '../types/transactions';

export type PaymentType = 'CHECK' | 'ZELLE' | 'TRANSFER' | null;
export type FlowDirection = 'INCOME' | 'EXPENSE';

/**
 * Returns 'INCOME' if money came INTO the account, 'EXPENSE' if it went out.
 * Uses Plaid's transactionClass field when available (stored in DB).
 * Falls back to amount sign for snapshot transactions (ClonedTransaction lacks the field).
 */
export function getFlowDirection(tx: PlaidTransaction): FlowDirection {
  const cls = (tx as any).transactionClass;
  if (cls === 'INCOME' || cls === 'EXPENSE') return cls;
  return tx.amount < 0 ? 'INCOME' : 'EXPENSE';
}

/**
 * Detects if a transaction is a check.
 * Uses checkNumber field first; falls back to text matching on name/originalDescription.
 */
export function isCheck(tx: PlaidTransaction): boolean {
  if (tx.checkNumber) return true;
  const text = `${tx.name || ''} ${tx.originalDescription || ''}`;
  return /\bCHE(QUE?|CK)\b/i.test(text);
}

/**
 * Returns the check number: the checkNumber field, else an explicit
 * "CHECK # N" / "CHECK NO. N" in the bank text. The #/NO marker is required
 * so account refs ("CHK XXXXXX3805") and ATM ids are never mistaken for one.
 */
export function getCheckNumber(tx: PlaidTransaction): string | null {
  if (tx.checkNumber) return tx.checkNumber;
  const text = `${tx.name || ''} ${tx.originalDescription || ''} ${tx.merchantName || ''}`;
  return text.match(/\bCHE(?:QUE|CK)\s*(?:#|NO\.?)\s*(\d+)/i)?.[1] ?? null;
}

/**
 * Detects if a transaction is a Zelle transfer.
 * Matches "ZELLE" in name, originalDescription, or counterparty names.
 */
export function isZelle(tx: PlaidTransaction): boolean {
  const counterpartyText = (tx as any).counterparties
    ? (tx as any).counterparties.map((c: any) => c.name || '').join(' ')
    : '';
  const text = `${tx.name || ''} ${tx.originalDescription || ''} ${counterpartyText}`;
  return /\bZELLE\b/i.test(text);
}

/**
 * Detects if a transaction is a bank transfer (ACH, wire, account transfer).
 * Uses Plaid's Personal Finance Category primary field.
 */
export function isTransfer(tx: PlaidTransaction): boolean {
  const primary = tx.personalFinanceCategoryPrimary;
  return primary === 'TRANSFER_IN' || primary === 'TRANSFER_OUT';
}

/**
 * Returns the payment type of a transaction.
 * CHECK > ZELLE > TRANSFER in precedence.
 */
export function getPaymentType(tx: PlaidTransaction): PaymentType {
  if (isCheck(tx)) return 'CHECK';
  if (isZelle(tx)) return 'ZELLE';
  if (isTransfer(tx)) return 'TRANSFER';
  return null;
}
